import {
  laptopMaxXL,
  mobileXXLargeMax,
  largeTabletMax,
  laptopLargeMax,
} from '@propertypal/shared/src/constants/mediaQueries';
import Link from 'next/link';
import styled from 'styled-components';

const userSectionMax = 'max-width: 1024px';
const userSectionMin = 'min-width: 650px';

const userSectionQuery = `@media screen and (${userSectionMax}) and (${userSectionMin})`;

interface UseWideDisplay {
  $useWideDisplay?: boolean;
}

interface UserSection {
  $userSection?: boolean;
}

interface Loading {
  $isLoading?: boolean;
}

interface SingleResult {
  isSingleResult?: boolean;
}

export const Container = styled.li<UseWideDisplay & UserSection & Loading & SingleResult>`
  position: relative;
  display: ${({ $userSection }) => ($userSection ? 'inline-' : '')}flex;
  flex-direction: column;
  ${({ $userSection }) =>
    $userSection
      ? `
        width: 100%;

        @media screen and (min-width: 1025px) {
          max-width: 340px;
        }
        
        ${userSectionQuery} {
          display: flex;
          flex: 1;
          flex-basis: 100%;
        }
      `
      : ``}

  ${(props) =>
    props.$useWideDisplay &&
    `
    grid-column: 1 / span 4;
    
    @media (${laptopLargeMax}) {
      grid-column: 1 / span 3;
    }
    
    @media (${largeTabletMax}) {
      grid-column: 1 / span 2;
    }

    @media (${mobileXXLargeMax}) {
      grid-column: 1 / span 1;
    }
  `}
`;

export const Wrapper = styled.div<UseWideDisplay & UserSection & Loading & { $premierColor?: string }>`
  background-color: ${({ theme, $isLoading }) => ($isLoading ? 'transparent' : theme.background)};
  display: flex;
  flex: 1;
  flex-direction: column;

  ${({ $userSection, theme, $isLoading }) =>
    !$userSection
      ? `
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      border-radius: 10px;

      &:hover {
        background-color: ${$isLoading ? 'transparent' : theme.backgroundLighter};
      }
      `
      : ``}

  ${(props) =>
    props.$useWideDisplay &&
    props.$premierColor &&
    `
    border: 2px solid ${props.$premierColor};
    border-radius: 6px;
    overflow: hidden;
  `}
`;

export const LinkContainer = styled(Link)<UseWideDisplay & UserSection>`
  display: flex;
  height: 100%;
  flex-direction: ${(props) => (props.$useWideDisplay ? 'column' : 'row')};

  @media (${mobileXXLargeMax}) {
    // Fixes box height on iOS 10
    height: auto;
  }

  &:visited {
    h2 {
      color: ${({ theme }) => theme.backgroundMid};
    }
    color: ${({ theme }) => theme.backgroundMid};
  }

  ${({ $userSection }) =>
    $userSection
      ? `      
      ${userSectionQuery} {
        flex: 1;
      }
    `
      : ``}
`;

export const Content = styled.div<UseWideDisplay & UserSection>`
  display: flex;
  flex-direction: column;
  flex: 1;

  ${({ $userSection }) =>
    $userSection &&
    `      
      ${userSectionQuery} {
        flex-direction: row;
        flex-grow: 1;
        width: 100%;
      }
    `}

  ${(props) =>
    props.$useWideDisplay &&
    `
    flex-direction: row;
    
    @media (${mobileXXLargeMax}) {
      flex-direction: column;
    }
  `}
`;

export const ImageContent = styled.div<UseWideDisplay & UserSection>`
  align-self: flex-start;
  width: 100%;

  ${({ $userSection }) =>
    $userSection &&
    `      
      ${userSectionQuery} {
        max-width: 380px;
      }
    `}

  ${(props) =>
    props.$useWideDisplay &&
    `
    display: flex;
    flex-direction: row;
    max-width: 1200px;
    flex-basis: 65%;
    
    @media (${laptopMaxXL}) {
      flex-basis: auto;
      width: 50%;
      flex-direction: column;
      max-width: 460px;
    }
    
    @media (${mobileXXLargeMax}) {
      width: 100%;
      max-width: none;
    }
  `}
`;

export const FavouriteBox = styled.div`
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 2;
`;

export const HeroImageBox = styled.div<UseWideDisplay>`
  display: flex;
  position: relative;
  overflow: hidden;
  flex: 1;

  ${(props) =>
    props.$useWideDisplay &&
    `
    max-width: 78%;
    
    @media (${laptopMaxXL}) {
      max-width: none;
    }
  `}
`;

// export const HeroImage = styled.img`
//   width: 100%;
//   height: auto;
//   align-self: flex-start;
// `;

export const ThumbImageContainer = styled.div<UseWideDisplay>`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${(props) =>
    props.$useWideDisplay &&
    `
    max-width: 22%;
    flex-direction: column;
    margin-left: 8px;
    
    @media (${laptopMaxXL}) {
      max-width: 100%;
      flex-direction: row;
      margin-left: 0px;
    }
  `}
`;

export const ThumbImage = styled.div<UseWideDisplay>`
  width: 32%;
  margin-top: 8px;

  ${(props) =>
    props.$useWideDisplay &&
    `
    width: 100%;
    
    &:first-child {
      margin-top: 0;
    }
    
    @media (${laptopMaxXL}) {
      width: 32%;
      
      &:first-child {
        margin-top: 8px;
      }
    }
  `}
`;

export const DescriptionBox = styled.div`
  padding: 16px 20px;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
`;

export const HeroNoImage = styled.img`
  width: 100%;
  background-color: ${(props) => props.theme.backgroundLighter};
`;
